import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "../../store";
import { WhsDeliveryListResponse, RequestWhsDeliveryParams } from "./types";
import { exportWhsDeliveryApi, getWhsDeliveryListApi } from "./api";

type WhsDeliveryListType = WhsDeliveryListResponse & {
  loading: boolean;
  error: string | null;
  success: string | null;
  currentParams: Partial<RequestWhsDeliveryParams> | null;
  exporting: { loading: boolean; error: string | null };
};

const initialState: WhsDeliveryListType = {
  items: null,
  loading: false,
  error: null,
  success: null,
  total: 0,
  page: 0,
  size: 0,
  currentParams: null,
  exporting: { loading: false, error: null },
};

const whsDeliveryList = createSlice({
  name: "whsDeliveryList",
  initialState: initialState,
  reducers: {
    getWhsDeliveryStart(state: WhsDeliveryListType) {
      state.loading = true;
      state.error = null;
    },
    getWhsDeliverySuccess(
      state: WhsDeliveryListType,
      action: PayloadAction<
        WhsDeliveryListResponse & { params?: RequestWhsDeliveryParams }
      >,
    ) {
      const { items, total, page, size, params } = action.payload;
      state.items = items;
      state.total = total;
      state.page = page;
      state.size = size;
      state.loading = false;
      if (params) {
        const { page, size, ...exportParams } = params;
        state.currentParams = exportParams;
      }
    },
    getWhsDeliveryFailure(
      state: WhsDeliveryListType,
      action: PayloadAction<string>,
    ) {
      state.loading = false;
      state.error = action.payload;
      state.items = null;
    },
    getExportFailure(
      state: WhsDeliveryListType,
      action: PayloadAction<string>,
    ) {
      state.exporting.loading = false;
      state.error = action.payload;
    },
    hideWarning(state: WhsDeliveryListType) {
      state.error = null;
    },
    cleanPackage(state: WhsDeliveryListType) {
      state.items = null;
    },
    startExport(state: WhsDeliveryListType) {
      state.exporting = {
        loading: true,
        error: null,
      };
    },
    successExport(state: WhsDeliveryListType) {
      state.exporting.loading = false;
      state.exporting.error = null;
    },
  },
});

export const {
  getWhsDeliveryStart,
  hideWarning,
  cleanPackage,
  getWhsDeliverySuccess,
  getWhsDeliveryFailure,
  getExportFailure,
  startExport,
  successExport,
} = whsDeliveryList.actions;

export default whsDeliveryList.reducer;

export const fetchWhsDeliveryList =
  (params: RequestWhsDeliveryParams): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getWhsDeliveryStart());
      const data = await getWhsDeliveryListApi(params);
      dispatch(getWhsDeliverySuccess({ ...data, params }));
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getWhsDeliveryFailure(error));
    }
  };

export const exportWhsDeliveryList =
  (params?: Partial<RequestWhsDeliveryParams>): AppThunk =>
  async (dispatch) => {
    dispatch(startExport());
    try {
      const { file, filename } = await exportWhsDeliveryApi(params);
      const url = window.URL.createObjectURL(new Blob([file]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getExportFailure(error));
    }
  };
